/* eslint-disable no-shadow */
import { END, buffers, eventChannel } from "redux-saga";
import {
    call,
    cancel,
    cancelled,
    delay,
    flush,
    fork,
    race,
    take,
    takeEvery,
} from "redux-saga/effects";
import noop from "lodash.noop";
import { ONE_SECOND } from "Globals/constants/constants";

import {
    LOAD_REQUESTED,
    SUBSCRIBE,
    UNSUBSCRIBE,
    UPDATE_READ_REQUESTED,
    defaultScopeId,
    failNotificationsLoad,
    failNotificationsUpdateRead,
    requestNotificationsLoad,
    requestNotificationsUpdateRead,
    succeedNotificationsLoad,
    succeedNotificationsUpdateRead
} from "./notifications.duck";

import {
    loadNotifications,
    subscribeNotifications,
    updateManyNotifications,
} from "Globals/dataTypes/notifications/notifications.api";

import {
    getId, injectApiWithContextFactory, injectBoundActionCreatorsFactory,
    takeFirstAndLatest,
    withRefreshRequest 
} from "Utils/sagaUtils";
import { injectBoundGQLClient } from "Utils/injectBoundGQLClient";


export const withBoundActions = injectBoundActionCreatorsFactory(
    {
        failNotificationsLoad,
        failNotificationsUpdateRead,
        requestNotificationsLoad,
        requestNotificationsUpdateRead,
        succeedNotificationsLoad,
        succeedNotificationsUpdateRead
    },
    getId,
    defaultScopeId
);

export const withApiContext = injectApiWithContextFactory(
    {
        loadNotifications,
        updateManyNotifications
    },
    injectBoundGQLClient
);


export function* loadNotificationsSaga({
    api: { loadNotifications = noop },
    actions: { succeedNotificationsLoad, failNotificationsLoad },
    resolve = noop,
    reject = noop,
}) {
    try {
        const { notifications: n, v2docs } = yield loadNotifications();
        const notifications = n?.edges?.map(({ node }) => node) || [];
        yield succeedNotificationsLoad({ notifications, v2docs });
        yield call(resolve);
    } catch (e) {
        yield failNotificationsLoad(e);
        yield call(reject, e);
    }
}

export function* updateReadNotificationsSaga({
    data,
    api: { updateManyNotifications = noop },
    actions: { succeedNotificationsUpdateRead, failNotificationsUpdateRead },
    resolve = noop,
    reject = noop,
}) {
    try {
        const updateNotifications = data.map(_id => ({ _id, read: true }));
        if (updateNotifications.length > 0){
            yield updateManyNotifications({ notifications: updateNotifications });
        }
        yield succeedNotificationsUpdateRead(updateNotifications);
        yield call(resolve);
    } catch (e) {
        yield failNotificationsUpdateRead({ error: e, notifications: data });
        yield call(reject, e);
    }
}

export const notificationsSubscription = subject => {
    return eventChannel(
        async emitter => {
            try {
                const sub = await subscribeNotifications(subject, emitter);
                return sub;
            } catch (e) {
                emitter(END);
            }
        },
        buffers.expanding(1) // buffer one invocation holding the most recent event
    );
};

export function* subscribeToNotifications({
    subject,
    actions: { succeedNotificationsLoad },
}) {
    const chan = yield call(notificationsSubscription, subject);
    try {
        while (true) {
            const notifications = yield flush(chan);
            if (notifications.length > 0) {
                const notificationItems = notifications.flatMap(({ data: { notificationsUpdated } }) => notificationsUpdated);
                yield succeedNotificationsLoad(notificationItems);
            }
            yield delay(ONE_SECOND);
        }
    } finally {
        if (yield cancelled()) {
            chan.close();
        }
    }
}

export function* subscribeNotificationsSaga() {
    const subscriptions = {};
    const tasks = {};
    while (true) {
        const { subscribe, unsubscribe } = yield race({
            subscribe: take(SUBSCRIBE),
            unsubscribe: take(UNSUBSCRIBE),
        });
        const {
            data: { userId, notificationId },
        } = subscribe || unsubscribe;
        const ids = [ userId, notificationId && `id-${notificationId}` ].filter(i => i);
        if (subscribe) {
            for (const subId of ids) {
                subscriptions[subId] = subscriptions[subId] || 0;
                subscriptions[subId]++;
                if (subscriptions[subId] === 1) {
                    tasks[subId] = yield fork(
                        withBoundActions(subscribeToNotifications),
                        { ...(subscribe || unsubscribe), subject: subId }
                    );
                }
            }
        }
        if (unsubscribe) {
            for (const subId of ids) {
                subscriptions[subId]--;
                if (subscriptions[subId] === 0) {
                    yield cancel(tasks[subId]);
                }
            }
        }
    }
}

export default () =>
    function* rootSaga() {
        yield takeFirstAndLatest(
            LOAD_REQUESTED,
            withRefreshRequest(
                withApiContext(withBoundActions(loadNotificationsSaga))
            ),
            ONE_SECOND * 5
        );
        yield takeEvery(
            UPDATE_READ_REQUESTED,
            withApiContext(withBoundActions(updateReadNotificationsSaga))
        );
        yield fork(subscribeNotificationsSaga);
    };
